import {Http} from '../../../Data/config/Http';
import {
  StoreDeliverySummaryResponseDTO,
  StoreDeliveryTimelineResponseDTO,
  StoreDeliveryTransactionResponseDTO,
  StoreDeliveryListResponseDTO
} from '../models/storeDeliveries.dto';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {StoreDeliveryFilters} from '../models/storeDeliveries.model';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';
import {AppError} from '../../../Data/config/AppError';
import {ErrorType} from '../../../Data/config/AppError';
import {AxiosError} from 'axios';

/**
 * Store Deliveries API — spec: GET /api/v1/dashboard/store-deliveries
 * Auth: Bearer token. Permission: inventory:read (backend enforces).
 * Response envelope: { stat_code, stat_message, data, error, pagination? }.
 */

export const StoreDeliveriesAPIDataSource = {
  /**
   * List deliveries (all or filtered)
   * GET /store-deliveries?storeId=&deliveredBy=&dateFrom=&dateTo=&productId=&sourceType=&sourceId=&page=&limit=
   */
  getList: async (
    filters: {
      storeId?: number;
      deliveredBy?: number;
      dateFrom?: string;
      dateTo?: string;
      productId?: number;
      sourceType?: string;
      sourceId?: number;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<StoreDeliveryListResponseDTO> => {
    try {
      const params: Record<string, unknown> = {
        page: filters.page ?? 1,
        limit: Math.min(filters.limit ?? 20, 100)
      };
      if (filters.storeId != null) params.storeId = filters.storeId;
      if (filters.deliveredBy != null) params.deliveredBy = filters.deliveredBy;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.productId != null) params.productId = filters.productId;
      if (filters.sourceType) params.sourceType = filters.sourceType;
      if (filters.sourceId != null) params.sourceId = filters.sourceId;

      const response = await Http.get<BaseResponse<StoreDeliveryListResponseDTO>>(
        API_ROUTES.STORE_DELIVERIES.LIST,
        {params}
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب قائمة التسليمات');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  /**
   * Summary by product (one store)
   * GET /store-deliveries/summary?store_id=&dateFrom=&dateTo=
   */
  getSummary: async (
    storeId: string,
    filters?: {from_date?: string; to_date?: string; dateFrom?: string; dateTo?: string}
  ): Promise<StoreDeliverySummaryResponseDTO> => {
    try {
      const params: Record<string, unknown> = { store_id: storeId };
      const from = filters?.dateFrom ?? filters?.from_date;
      const to = filters?.dateTo ?? filters?.to_date;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;

      const response = await Http.get<BaseResponse<StoreDeliverySummaryResponseDTO>>(
        API_ROUTES.STORE_DELIVERIES.SUMMARY,
        {params}
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب ملخص تسليمات المتجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  /**
   * Timeline for one store
   * GET /store-deliveries/timeline?store_id=&dateFrom=&dateTo=&page=&limit=
   */
  getTimeline: async (
    storeId: string,
    filters: StoreDeliveryFilters = {}
  ): Promise<StoreDeliveryTimelineResponseDTO> => {
    try {
      const params: Record<string, unknown> = {
        store_id: storeId,
        page: filters.page ?? 1,
        limit: Math.min(filters.limit ?? 20, 100)
      };
      if (filters.from_date) params.dateFrom = filters.from_date;
      if (filters.to_date) params.dateTo = filters.to_date;
      if (filters.action_type) params.action_type = filters.action_type;
      if (filters.source_type) params.sourceType = filters.source_type;

      const response = await Http.get<BaseResponse<StoreDeliveryTimelineResponseDTO>>(
        API_ROUTES.STORE_DELIVERIES.TIMELINE,
        {params}
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب جدول تسليمات المتجر');
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  /**
   * Single delivery detail by id
   * GET /store-deliveries/:id
   * On 404 (not found or forbidden): show "Delivery not found" per spec.
   */
  getTransaction: async (
    transactionId: string
  ): Promise<StoreDeliveryTransactionResponseDTO> => {
    try {
      const response = await Http.get<BaseResponse<StoreDeliveryTransactionResponseDTO>>(
        API_ROUTES.STORE_DELIVERIES.BY_ID(transactionId)
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب تفاصيل المعاملة');
    } catch (error: unknown) {
      const status = (error as AxiosError<{ stat_code?: number }>)?.response?.status;
      if (status === 404) {
        throw new AppError('Delivery not found', ErrorType.NOT_FOUND, { status: 404 }, 404, error);
      }
      handleDataSourceError(error);
    }
  }
};
